import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Download, Plus, Tractor, Upload } from "lucide-react";
import type { EquipmentFormOptions, EquipmentStatus, EquipmentSummary } from "@crewledger/shared/schemas";
import { EmptyState, ListTableSkeleton, PageHeader, PaginationBar, useToast } from "@/components/design";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { formatCurrency, formatHours } from "@/features/dashboard/utils";
import { BETA_EMPTY_STATES } from "@/lib/beta-empty-states";
import {
  EQUIPMENT_STATUS_LABELS,
  EQUIPMENT_TYPE_LABELS,
  downloadEquipmentImportTemplate,
  fetchEquipment,
  fetchEquipmentOptions,
  updateEquipmentStatus,
} from "./api";
import { EquipmentStatusBadge, EquipmentTypeBadge } from "./components/equipment-badges";
import { useEquipmentPermissions } from "./use-equipment-permissions";

const ALL_STATUSES: EquipmentStatus[] = [
  "AVAILABLE",
  "IN_USE",
  "MAINTENANCE",
  "OUT_OF_SERVICE",
];

const PAGE_SIZE = 50;

export function EquipmentListPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { canWrite, canViewCosts } = useEquipmentPermissions();
  const [items, setItems] = useState<EquipmentSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [options, setOptions] = useState<EquipmentFormOptions | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [q, setQ] = useState("");
  const debouncedQ = useDebouncedValue(q, 300);
  const [siteId, setSiteId] = useState("");
  const [type, setType] = useState("");
  const [status, setStatus] = useState("");
  const [updatingId, setUpdatingId] = useState<string | null>(null);

  useEffect(() => {
    fetchEquipmentOptions()
      .then((res) => { setOptions(res.data); })
      .catch(() => { setOptions(null); });
  }, []);

  useEffect(() => {
    setPage(1);
  }, [debouncedQ, siteId, type, status]);

  useEffect(() => {
    setLoading(true);
    setError(null);
    fetchEquipment({
      page,
      pageSize: PAGE_SIZE,
      q: debouncedQ || undefined,
      siteId: siteId || undefined,
      type: type || undefined,
      status: status || undefined,
    })
      .then((res) => {
        setItems(res.data);
        setTotal(res.meta.total ?? res.data.length);
      })
      .catch((err: unknown) =>
        { setError(err instanceof Error ? err.message : "Failed to load equipment"); },
      )
      .finally(() => { setLoading(false); });
  }, [page, debouncedQ, siteId, type, status]);

  const handleStatusChange = async (id: string, newStatus: EquipmentStatus) => {
    setUpdatingId(id);
    try {
      await updateEquipmentStatus(id, newStatus);
      toast({ variant: "success", title: "Status updated" });
      setItems((prev) =>
        prev.map((item) => (item.id === id ? { ...item, status: newStatus } : item)),
      );
    } catch (err) {
      toast({
        variant: "error",
        title: "Update failed",
        description: err instanceof Error ? err.message : undefined,
      });
    } finally {
      setUpdatingId(null);
    }
  };

  const hasActiveFilters = Boolean(debouncedQ || siteId || type || status);
  const showBetaEmpty = items.length === 0 && !hasActiveFilters;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Equipment Log"
        description="Track fleet usage, hourly costs, and site assignments."
        actions={
          canWrite ? (
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  void downloadEquipmentImportTemplate().catch((err: unknown) => {
                    toast({
                      variant: "error",
                      title: "Download failed",
                      description: err instanceof Error ? err.message : undefined,
                    });
                  });
                }}
              >
                <Download className="mr-1.5 h-4 w-4" />
                Download template
              </Button>
              <Link to="/equipment/import">
                <Button variant="outline">
                  <Upload className="mr-1.5 h-4 w-4" />
                  Import CSV
                </Button>
              </Link>
              <Link to="/equipment/new">
                <Button>
                  <Plus className="mr-1.5 h-4 w-4" />
                  New equipment
                </Button>
              </Link>
            </div>
          ) : undefined
        }
      />

      {showBetaEmpty ? null : (
      <div className="grid gap-3 rounded-xl border bg-card-white p-4 sm:grid-cols-2 lg:grid-cols-4">
        <Input placeholder="Search ID or name…" value={q} onChange={(e) => { setQ(e.target.value); }} />
        <select
          value={siteId}
          onChange={(e) => { setSiteId(e.target.value); }}
          className="h-9 rounded-md border border-input bg-transparent px-3 text-sm"
        >
          <option value="">All sites</option>
          {options?.sites.map((s) => (
            <option key={s.id} value={s.id}>
              {s.code} — {s.name}
            </option>
          ))}
        </select>
        <select
          value={type}
          onChange={(e) => { setType(e.target.value); }}
          className="h-9 rounded-md border border-input bg-transparent px-3 text-sm"
        >
          <option value="">All types</option>
          {Object.entries(EQUIPMENT_TYPE_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
        <select
          value={status}
          onChange={(e) => { setStatus(e.target.value); }}
          className="h-9 rounded-md border border-input bg-transparent px-3 text-sm"
        >
          <option value="">All statuses</option>
          {Object.entries(EQUIPMENT_STATUS_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
      </div>
      )}

      {error ? (
        <EmptyState icon={Tractor} title="Could not load equipment" description={error} />
      ) : loading ? (
        <ListTableSkeleton rows={8} cols={9} />
      ) : items.length === 0 ? (
        <EmptyState
          icon={Tractor}
          title={showBetaEmpty ? BETA_EMPTY_STATES.equipment.title : "No matching equipment"}
          description={
            showBetaEmpty
              ? BETA_EMPTY_STATES.equipment.description
              : "Try adjusting your search or filters to find fleet assets."
          }
          actionLabel={showBetaEmpty && canWrite ? BETA_EMPTY_STATES.equipment.actionLabel : undefined}
          onAction={showBetaEmpty && canWrite ? () => { navigate("/equipment/new"); } : undefined}
        />
      ) : (
        <div className="overflow-hidden rounded-xl border bg-card-white shadow-[var(--shadow-card)]">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[960px] text-sm">
            <thead>
              <tr className="border-b bg-bg-light text-left text-xs uppercase tracking-wider text-muted-foreground">
                <th className="px-4 py-3">Equipment ID</th>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3">Site</th>
                <th className="px-4 py-3">Hourly cost</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Period hrs</th>
                <th className="px-4 py-3">Est. cost</th>
                <th className="px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id} className="border-b border-border/60 last:border-0 hover:bg-bg-light/50">
                  <td className="px-4 py-3 font-mono font-medium text-navy-deep">{item.equipmentId}</td>
                  <td className="px-4 py-3 font-medium">{item.name}</td>
                  <td className="px-4 py-3">
                    <EquipmentTypeBadge type={item.type} />
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">{item.siteCode ?? "—"}</td>
                  <td className="px-4 py-3 tabular-nums">
                    {canViewCosts && item.hourlyCost != null
                      ? `${formatCurrency(item.hourlyCost)}/hr`
                      : "—"}
                  </td>
                  <td className="px-4 py-3">
                    {canWrite ? (
                      <select
                        value={item.status}
                        disabled={updatingId === item.id}
                        onChange={(e) =>
                          void handleStatusChange(item.id, e.target.value as EquipmentStatus)
                        }
                        className="h-8 rounded-md border border-input bg-transparent px-2 text-xs"
                      >
                        {ALL_STATUSES.map((s) => (
                          <option key={s} value={s}>
                            {EQUIPMENT_STATUS_LABELS[s]}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <EquipmentStatusBadge status={item.status} />
                    )}
                  </td>
                  <td className="px-4 py-3 tabular-nums">{formatHours(item.periodUsageHours)}</td>
                  <td className="px-4 py-3 tabular-nums">
                    {canViewCosts && item.estimatedPeriodCost != null
                      ? formatCurrency(item.estimatedPeriodCost)
                      : "—"}
                  </td>
                  <td className="px-4 py-3">
                    <Link
                      to={`/equipment/${item.id}`}
                      className="font-medium text-orange-construction hover:underline"
                    >
                      View
                    </Link>
                    {canWrite ? (
                      <>
                        {" · "}
                        <Link
                          to={`/equipment/${item.id}/edit`}
                          className="text-muted-foreground hover:underline"
                        >
                          Edit
                        </Link>
                      </>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
          <PaginationBar page={page} pageSize={PAGE_SIZE} total={total} onPageChange={setPage} />
        </div>
      )}
    </div>
  );
}
